import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { bookingApi } from '../utils/bookingApi';
import DashboardPanel from './DashboardPanel';
import { NOTE_ISSUES } from './payments/MembershipPaymentWarning';
import { Chevron, InlineError } from './ui';
import '../pages/booking/booking-shared.css';

const money = (pence) => `£${(pence / 100).toFixed(2)}`;
const nameOf = (m) => `${m.gymnast.firstName} ${m.gymnast.lastName}`;

// Home page of staff who can see payments: memberships behind on payments, as last checked
// against Stripe. An admin can acknowledge one to take it off the list; it
// comes back if the problem changes. Below them, first payments still to come
// that cover this month (nothing owed yet, so no count and no Acknowledge).
// Shows nothing when there is nothing new.
export default function PaymentsBehindCard() {
  const { can } = useAuth();
  const seesPayments = can('payments');
  const managesPayments = can('payments', 'MANAGE');
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState([]);
  // Acknowledged while this page is open: kept in place with an Undo
  const [justAcked, setJustAcked] = useState(() => new Set());
  const [showAcked, setShowAcked] = useState(false);
  const [busy, setBusy] = useState(() => new Set());
  const [error, setError] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = () => {
    setLoadFailed(false);
    bookingApi.getDelinquentMemberships()
      .then(res => {
        const all = res.data.memberships || [];
        setRows(all.filter(m => !NOTE_ISSUES.includes(m.paymentIssue)));
        setNotes(all.filter(m => NOTE_ISSUES.includes(m.paymentIssue)));
      })
      .catch(() => setLoadFailed(true));
  };

  useEffect(() => {
    if (!seesPayments) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seesPayments]);

  const setAck = async (m, ack) => {
    if (busy.has(m.id)) return;
    setBusy(set => new Set(set).add(m.id));
    setError(null);
    try {
      const res = ack
        ? await bookingApi.acknowledgePaymentIssue(m.id, { issue: m.paymentIssue, amount: m.paymentIssueAmount })
        : await bookingApi.unacknowledgePaymentIssue(m.id);
      setRows(list => list.map(r => (r.id === m.id ? { ...r, paymentIssueAckedAt: res.data.paymentIssueAckedAt } : r)));
      setJustAcked(set => {
        const next = new Set(set);
        if (ack) next.add(m.id); else next.delete(m.id);
        return next;
      });
    } catch (err) {
      setError(err.response?.data?.error || `Couldn't update ${nameOf(m)}. Try again.`);
    } finally {
      setBusy(set => {
        const next = new Set(set);
        next.delete(m.id);
        return next;
      });
    }
  };

  const open = rows.filter(m => !m.paymentIssueAckedAt);
  const recent = rows.filter(m => m.paymentIssueAckedAt && justAcked.has(m.id));
  const older = rows.filter(m => m.paymentIssueAckedAt && !justAcked.has(m.id));

  const anyBehind = open.length > 0 || recent.length > 0;
  if (seesPayments && loadFailed) {
    return (
      <DashboardPanel
        className="dashboard-comp"
        headerClassName="dashboard-comp__header"
        title={<span className="dashboard-comp__title">Payments behind</span>}
      >
        <InlineError onRetry={load}>Couldn't check who is behind on payments.</InlineError>
      </DashboardPanel>
    );
  }
  if (!seesPayments || (!anyBehind && notes.length === 0)) return null;

  const undoRow = (m) => (
    <li key={m.id} className="dashboard-comp__item dashboard-comp__item--acked">
      <div className="dashboard-comp__item-main">
        <div className="dashboard-comp__item-name">{nameOf(m)} acknowledged</div>
      </div>
      {managesPayments && <button type="button" className="bk-btn bk-btn--sm" onClick={() => setAck(m, false)} disabled={busy.has(m.id)}>Undo</button>}
    </li>
  );

  return (
    <DashboardPanel
      className={`dashboard-comp${anyBehind ? ' dashboard-payments-behind' : ''}`}
      headerClassName="dashboard-comp__header"
      title={<span className="dashboard-comp__title">{anyBehind ? 'Payments behind' : 'First payments to come'}</span>}
      badge={open.length > 0 && <span className="dashboard-comp__badge">{open.length} member{open.length === 1 ? '' : 's'}</span>}
    >
      <ul className="dashboard-comp__list">
        {rows.filter(m => !m.paymentIssueAckedAt || justAcked.has(m.id)).map(m => (m.paymentIssueAckedAt ? undoRow(m) : (
          <li key={m.id} className="dashboard-comp__item">
            <div className="dashboard-comp__item-main">
              <div className="dashboard-comp__item-name">
                <Link to={`/booking/admin/memberships?membership=${m.id}`}>{nameOf(m)}</Link>
                {m.paymentIssueAmount > 0 && <> · {money(m.paymentIssueAmount)}</>}
              </div>
              <div className="dashboard-comp__item-meta">{m.paymentIssueDetail}</div>
            </div>
            {managesPayments && (
              <button
                type="button"
                className="bk-btn bk-btn--sm"
                onClick={() => setAck(m, true)}
                disabled={busy.has(m.id)}
                aria-label={`Acknowledge ${nameOf(m)}`}
              >
                Acknowledge
              </button>
            )}
          </li>
        )))}
        {showAcked && older.map(undoRow)}
      </ul>
      {notes.length > 0 && (
        <>
          {anyBehind && <div className="dashboard-comp__subhead">First payments to come</div>}
          <ul className="dashboard-comp__list">
            {notes.map(m => (
              <li key={m.id} className="dashboard-comp__item">
                <div className="dashboard-comp__item-main">
                  <div className="dashboard-comp__item-name">
                    <Link to={`/booking/admin/memberships?membership=${m.id}`}>{nameOf(m)}</Link>
                  </div>
                  <div className="dashboard-comp__item-meta">{m.paymentIssueDetail}</div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {error && <p className="dashboard-comp__error" role="alert">{error}</p>}
      {older.length > 0 && (
        <div className="dashboard-comp__footer">
          <button type="button" className="bk-btn bk-btn--sm" onClick={() => setShowAcked(s => !s)} aria-expanded={showAcked}>
            {showAcked ? 'Hide acknowledged' : `Show ${older.length} acknowledged`}
            <Chevron />
          </button>
        </div>
      )}
    </DashboardPanel>
  );
}
